/**
 * KAN-751: инструкции оператора по частям.
 *
 * - useOperatorSectionsRegistry: реестр частей (заголовки, подсказки).
 * - usePromptSectionsPreview: превью собранного текста по текущим значениям формы.
 * - isPromptSectionsEmpty / promptSectionsEqual: сравнения для формы.
 * - parsePromptSectionsErrors: ошибки 400 по частям из ответа DRF.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { operatorSectionsApi } from '@/services/api/operatorSections';
import type { OperatorSectionMeta, PromptSections } from '@/types/user';

/** Лимиты длины частей (как на бэкенде): extra длиннее, остальные 4000. */
export const DEFAULT_SECTION_MAX_LENGTH = 4000;
export const SECTION_MAX_LENGTH: Record<string, number> = { extra: 10000 };

/** Лимит части: из реестра, иначе из таблицы, иначе общий. */
export function sectionMaxLength(section: Pick<OperatorSectionMeta, 'key' | 'max_length'>): number {
  return section.max_length ?? SECTION_MAX_LENGTH[section.key] ?? DEFAULT_SECTION_MAX_LENGTH;
}

export const operatorSectionsQueryKey = ['operator-sections'] as const;

export function useOperatorSectionsRegistry() {
  return useQuery({
    queryKey: operatorSectionsQueryKey,
    queryFn: operatorSectionsApi.list,
    staleTime: Infinity,
  });
}

export function usePromptSectionsPreview(userId: string) {
  return useMutation({
    mutationFn: (sections: PromptSections) => operatorSectionsApi.preview(userId, sections),
  });
}

/** Все части пустые (или пробелы). */
export function isPromptSectionsEmpty(sections: PromptSections | null | undefined): boolean {
  return Object.values(sections ?? {}).every(v => !v || !v.trim());
}

/** Равенство по содержимому: пустая часть и отсутствующий ключ считаются одинаковыми. */
export function promptSectionsEqual(a: PromptSections, b: PromptSections): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? '') !== (b[key] ?? '')) return false;
  }
  return true;
}

export interface PromptSectionsErrors {
  /** Ошибки по ключу части. */
  byKey: Record<string, string>;
  /** Ошибка поля целиком (например, неверный формат). */
  general: string | null;
}

const toText = (value: unknown): string =>
  Array.isArray(value) ? value.map(String).join(' ') : String(value);

/**
 * Достаёт ошибки prompt_sections из тела 400:
 * {"prompt_sections": {"prices": ["..."]}} -> byKey,
 * {"prompt_sections": ["..."]} -> general.
 */
export function parsePromptSectionsErrors(data: unknown): PromptSectionsErrors {
  const result: PromptSectionsErrors = { byKey: {}, general: null };
  if (!data || typeof data !== 'object') return result;
  const raw = (data as Record<string, unknown>).prompt_sections;
  if (raw === undefined || raw === null) return result;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      result.byKey[key] = toText(value);
    }
  } else {
    result.general = toText(raw);
  }
  return result;
}
